import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, X, LogOut, Moon, Sun, CheckCircle, MessageSquare, DollarSign } from 'lucide-react';

const STATUS_COLORS = {
    Pending:   { bg: '#fef9c3', color: '#854d0e', border: '#fde047' },
    Reviewed:  { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
    Scheduled: { bg: '#ccfdf7', color: '#0f766e', border: '#5eead4' },
    Completed: { bg: '#dcfce7', color: '#166534', border: '#86efac' },
    Archived:  { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
};

// Session heartbeat interval (ms)
const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes


export default function DoctorDashboard() {
    const navigate = useNavigate();
    const [referrals, setReferrals]     = useState([]);
    const [search, setSearch]           = useState('');
    const [statusFilter, setStatus]     = useState('');
    const [loading, setLoading]         = useState(true);
    const [selected, setSelected]       = useState(null);
    const [noteText, setNoteText]       = useState('');
    const [savingNote, setSavingNote]   = useState(false);
    const [darkMode, setDarkMode]       = useState(false);
    const [sessionVerified, setSessionVerified] = useState(false);
    const [userName, setUserName] = useState('Doctor');
    const [activeTab, setActiveTab] = useState('referrals');

    const [payments, setPayments]                 = useState([]);
    const [paymentsLoading, setPaymentsLoading]   = useState(false);

    // ── Security: authenticated fetch wrapper ─────────────────────────────────
    // Auto-logout on 401/403 responses
    const apiFetch = async (url, opts = {}) => {
        const res = await fetch(url, {
            ...opts,
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
        });
        if (res.status === 401 || res.status === 403) {
            logout();
            throw new Error('Session expired or unauthorized');
        }
        return res;
    };

    // ── Security: verify session + role on mount ──────────────────────────────
    useEffect(() => {
        const verifySession = async () => {
            try {
                const res = await fetch('/api/auth/me', { credentials: 'include' });
                if (!res.ok) { logout(); return; }
                const data = await res.json();
                if (data.role !== 'doctor') {
                    navigate(data.role === 'desk' ? '/desk' : '/login', { replace: true });
                    return;
                }
                setUserName(data.name || 'Doctor');
                localStorage.setItem('trf_role', data.role);
                localStorage.setItem('trf_name', data.name);
                setSessionVerified(true);
            } catch {
                logout();
            }
        };
        verifySession();

        // ── Security: periodic session heartbeat ──────────────────────────────
        const heartbeat = setInterval(async () => {
            try {
                const res = await fetch('/api/auth/me', { credentials: 'include' });
                if (!res.ok) logout();
            } catch { /* network errors handled gracefully */ }
        }, SESSION_CHECK_INTERVAL);
        return () => clearInterval(heartbeat);
    }, []);

    const fetchReferrals = async () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (statusFilter) params.set('status', statusFilter);
        if (search) params.set('search', search);
        try {
            const res = await apiFetch(`/api/referrals?${params}`);
            setReferrals(await res.json());
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const fetchPayments = async () => {
        setPaymentsLoading(true);
        try {
            const res = await apiFetch('/api/payments');
            if (res.ok) setPayments(await res.json());
        } finally { setPaymentsLoading(false); }
    };

    useEffect(() => { if (sessionVerified && activeTab === 'referrals') fetchReferrals(); }, [search, statusFilter, sessionVerified, activeTab]);
    useEffect(() => { if (sessionVerified && activeTab === 'payments') fetchPayments(); }, [activeTab, sessionVerified]);

    const saveNote = async () => {
        if (!selected) return;
        setSavingNote(true);
        try {
            const res = await apiFetch(`/api/referrals/${selected._id}`, {
                method: 'PUT',
                body: JSON.stringify({ internalNotes: noteText, status: selected.status }),
            });
            if (res.ok) {
                const { referral } = await res.json();
                setSelected(referral);
                fetchReferrals();
            }
        } finally { setSavingNote(false); }
    };

    const logout = async () => {
        await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
        localStorage.removeItem('trf_role');
        localStorage.removeItem('trf_name');
        navigate('/login');
    };

    const openDetail = (r) => { setSelected(r); setNoteText(r.internalNotes || ''); };

    // ── Security: don't render until session is verified ──────────────────
    if (!sessionVerified) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--off-white)' }}>
                <span className="trf-spinner" style={{ width: '2rem', height: '2rem' }} />
            </div>
        );
    }

    return (
        <div className={`app${darkMode ? ' dark' : ''}`}>
            {/* Nav */}
            <nav className="dash-nav">
                <div className="dash-nav-brand">
                    <span style={{ color: '#fff' }}>Titanium</span>&nbsp;
                    <span style={{ color: 'var(--teal)' }}>Surgical Arts</span>
                    <span className="dash-nav-role">Doctor View</span>
                </div>
                <div className="dash-nav-actions">
                    <span className="dash-nav-user">{userName}</span>
                    <button className="dash-icon-btn" onClick={() => setDarkMode(d => !d)} aria-label="Toggle dark mode">
                        {darkMode ? <Sun size={16} /> : <Moon size={16} />}
                    </button>
                    <button className="dash-icon-btn" onClick={logout} aria-label="Log out">
                        <LogOut size={16} />
                    </button>
                </div>
            </nav>

            <div className="dash-body">
                {/* Tabs */}
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid var(--gray-200)', paddingBottom: '0' }}>
                    {[{ id: 'referrals', label: 'Referrals', Icon: FileText }, { id: 'payments', label: 'Payments', Icon: DollarSign }].map(({ id, label, Icon }) => (
                        <button key={id} onClick={() => setActiveTab(id)} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1rem', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: activeTab === id ? 'var(--teal)' : 'var(--text-muted)', borderBottom: activeTab === id ? '2px solid var(--teal)' : '2px solid transparent', marginBottom: '-2px', transition: 'color 0.15s' }}>
                            <Icon size={14} />{label}
                        </button>
                    ))}
                </div>

                {activeTab === 'referrals' && (<>
                {/* Toolbar */}
                <div className="dash-toolbar">
                    <h1 className="dash-page-title">Referrals</h1>
                    <div className="dash-toolbar-right">
                        <div className="dash-search-wrap">
                            <Search size={15} className="dash-search-icon" />
                            <input
                                className="trf-input dash-search"
                                placeholder="Search patient or doctor…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                        <select className="trf-input" style={{ width: 'auto' }} value={statusFilter} onChange={e => setStatus(e.target.value)}>
                            <option value="">All Statuses</option>
                            {Object.keys(STATUS_COLORS).map(s => <option key={s}>{s}</option>)}
                        </select>
                    </div>
                </div>

                {/* Stats */}
                <div className="dash-stats">
                    {Object.entries(STATUS_COLORS).map(([s, c]) => (
                        <button key={s} className="dash-stat-pill" style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }} onClick={() => setStatus(statusFilter === s ? '' : s)}>
                            {s}: <strong>{referrals.filter(r => r.status === s).length}</strong>
                        </button>
                    ))}
                </div>

                {/* Table */}
                <div className="trf-card" style={{ padding: 0, overflow: 'hidden' }}>
                    {loading ? (
                        <div className="dash-empty">Loading…</div>
                    ) : referrals.length === 0 ? (
                        <div className="dash-empty">No referrals found.</div>
                    ) : (
                        <table className="dash-table">
                            <thead>
                                <tr>
                                    <th>Date</th><th>Patient</th><th>Referring Dr.</th>
                                    <th>Procedures</th><th>Status</th><th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {referrals.map(r => {
                                    const sc = STATUS_COLORS[r.status] || STATUS_COLORS.Pending;
                                    return (
                                        <tr key={r._id}>
                                            <td>{r.date || '—'}</td>
                                            <td><strong>{r.patientName}</strong><br /><span className="dash-sub">{r.patientPhone}</span></td>
                                            <td>{r.referringDr}</td>
                                            <td>{r.procedures?.length || 0} procedure{r.procedures?.length !== 1 ? 's' : ''}</td>
                                            <td><span className="dash-badge" style={{ background: sc.bg, color: sc.color, borderColor: sc.border }}>{r.status}</span></td>
                                            <td>
                                                <button className="dash-action-btn" onClick={() => openDetail(r)} title="View details">
                                                    <FileText size={15} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
                </>)}

                {/* ── Payments Tab ── */}
                {activeTab === 'payments' && (
                    <>
                        <div className="dash-toolbar">
                            <h1 className="dash-page-title">Payment History</h1>
                        </div>
                        <div className="trf-card" style={{ padding: 0, overflow: 'hidden' }}>
                            {paymentsLoading ? <div className="dash-empty">Loading payments…</div> : payments.length === 0 ? <div className="dash-empty">No payments found.</div> : (
                                <table className="dash-table">
                                    <thead><tr><th>Date</th><th>Patient</th><th>Amount</th><th>Procedure</th><th>Status</th><th>Clover ID / Invoice</th></tr></thead>
                                    <tbody>
                                        {payments.map(p => (
                                            <tr key={p._id}>
                                                <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                                                <td><strong>{p.patientName}</strong><br /><span className="dash-sub">{p.patientEmail}</span></td>
                                                <td style={{ color: '#166534', fontWeight: 'bold' }}>${p.amount.toFixed(2)}</td>
                                                <td>{p.procedureType}</td>
                                                <td><span className="dash-badge" style={{ background: p.status === 'SUCCESS' ? '#dcfce7' : '#fef2f2', color: p.status === 'SUCCESS' ? '#166534' : '#dc2626', borderColor: p.status === 'SUCCESS' ? '#86efac' : '#fecaca' }}>{p.status}</span></td>
                                                <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{p.cloverChargeId}<br />{p.invoiceNumber ? `INV: ${p.invoiceNumber}` : ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Detail Drawer */}
            {selected && (
                <div className="dash-overlay" onClick={() => setSelected(null)}>
                    <div className="dash-drawer" onClick={e => e.stopPropagation()}>
                        <div className="dash-drawer-header">
                            <div>
                                <h2 className="dash-drawer-title">{selected.patientName}</h2>
                                <p className="dash-drawer-sub">Submitted {new Date(selected.submittedAt).toLocaleDateString()}</p>
                            </div>
                            <button className="dash-icon-btn" onClick={() => setSelected(null)}><X size={18} /></button>
                        </div>
                        <div className="dash-drawer-body">
                            <div className="dash-detail-grid">
                                <div><span className="dash-detail-label">DOB</span><span>{selected.patientDOB || '—'}</span></div>
                                <div><span className="dash-detail-label">Phone</span><span>{selected.patientPhone || '—'}</span></div>
                                <div><span className="dash-detail-label">Email</span><span>{selected.patientEmail || '—'}</span></div>
                                <div><span className="dash-detail-label">Referring Dr.</span><span>{selected.referringDr}</span></div>
                                <div><span className="dash-detail-label">Dr. Phone</span><span>{selected.phone || '—'}</span></div>
                                <div><span className="dash-detail-label">Radiographs Sent</span><span>{selected.radiographsSent ? 'Yes' : 'No'}</span></div>
                                <div><span className="dash-detail-label">Call Before</span><span>{selected.callBeforeTreatment ? 'Yes' : 'No'}</span></div>
                                <div><span className="dash-detail-label">Status</span>
                                    <span className="dash-badge" style={{ background: STATUS_COLORS[selected.status]?.bg, color: STATUS_COLORS[selected.status]?.color, borderColor: STATUS_COLORS[selected.status]?.border }}>{selected.status}</span>
                                </div>
                            </div>
                            {selected.procedures?.length > 0 && (
                                <div className="dash-detail-section">
                                    <span className="dash-detail-label">Procedures</span>
                                    <div className="dash-tag-list">{selected.procedures.map(p => <span key={p} className="dash-tag">{p}</span>)}</div>
                                </div>
                            )}
                            {selected.selectedTeeth?.length > 0 && (
                                <div className="dash-detail-section">
                                    <span className="dash-detail-label">Selected Teeth</span>
                                    <div className="dash-tag-list">{selected.selectedTeeth.map(t => <span key={t} className="dash-tag">#{t}</span>)}</div>
                                </div>
                            )}
                            {selected.comments && (
                                <div className="dash-detail-section">
                                    <span className="dash-detail-label">Comments</span>
                                    <p className="dash-detail-text">{selected.comments}</p>
                                </div>
                            )}
                            <div className="dash-detail-section">
                                <label className="dash-detail-label"><MessageSquare size={12} style={{ marginRight: 4 }} />Internal Notes</label>
                                <textarea className="trf-input" rows={4} value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add clinical notes…" style={{ resize: 'vertical', marginTop: '0.4rem' }} />
                                <button className="trf-btn-submit" style={{ marginTop: '0.75rem' }} onClick={saveNote} disabled={savingNote}>
                                    {savingNote ? <><span className="trf-spinner" /> Saving…</> : <><CheckCircle size={14} /> Save Note</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
